import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { isEmpty } from "react-admin";
import { db } from "../../firebase";
import { Typography } from "@material-ui/core";
import { ErrorDetailList } from "../../components/templates";

export const checkField = (tempField, consoleError) => {
  const expectedFields = [
    "id",
    "fullName",
    "cardNumber",
    "employeeNumber",
    "credit",
    "recurringCredit",
    "type.id",
    "type.name",
    "email",
    "mobile",
    "isActive",
    "createdAt",
    "updatedAt",
    "recurringCreditType",
    "costCenter",
  ];

  const field = tempField[0];
  if (field.length > expectedFields.length) {
    consoleError.push("Cannot add extra columns in this document");
  }

  expectedFields.forEach((expectedField, index) => {
    if (field[index] !== expectedField) {
      consoleError.push(`Column ${index + 1} should be ${expectedField}`);
    }
  });

  return consoleError;
};

export const randomIdMaker = (length = 28) => {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  return Array.from({ length }, () =>
    characters.charAt(Math.floor(Math.random() * characters.length)),
  ).join("");
};

export const convert2string = (errors) => errors.join("; ");

export const csv2json = async (clientId, tempResult, consoleError) => {
  const jsonObjectId = [];
  const jsonObjectArray = [];
  let count = 2;

  const employeeTypeList = await getEmployeeType(clientId);

  const validators = {
    checkName: (name) => {
      if (isEmpty(name)) {
        consoleError.push(`row ${count} column B: name cannot be left empty`);
        return "";
      }
      return name;
    },
    checkCard: (cardNumber) => {
      if (isEmpty(cardNumber)) {
        consoleError.push(
          `row ${count} column C: card number cannot be left empty`,
        );
        return "";
      } else if (isNaN(cardNumber)) {
        consoleError.push(
          `row ${count} column C: card number can only contain digits`,
        );
        return "";
      }
      return cardNumber;
    },
    checkCredit: (credit) => {
      if (isEmpty(credit)) {
        consoleError.push(`row ${count} column E: credit should not be empty`);
        return "";
      } else if (isNaN(credit)) {
        consoleError.push(`row ${count} column E: credit should be all digits`);
        return "";
      }
      return Number(credit);
    },
    checkRecurringCredit: (recurringCredit) => {
      if (isNaN(recurringCredit)) {
        consoleError.push(
          `row ${count} column F: Recurring credit should be all digits`,
        );
        return "";
      }
      return Number(recurringCredit);
    },
    checkTypeId: (typeId, typeName) => {
      if (!isEmpty(typeId)) return typeId;
      const idFirestore = employeeTypeList.find(
        (employee) => employee.type_name === typeName,
      );
      if (!idFirestore)
        consoleError.push(`row ${count} column G: type.id not found.`);
      return idFirestore ? idFirestore.type_id : "";
    },
    checkTypeName: (typeName) => {
      if (!isEmpty(typeName)) return typeName;
      consoleError.push(`row ${count} column H: type.name cannot be empty.`);
      return "";
    },
  };

  tempResult.forEach((result) => {
    const jsonObject = {
      fullName: validators.checkName(result[1]),
      cardNumber: validators.checkCard(result[2]),
      employeeNumber: result[3],
      credit: validators.checkCredit(result[4]),
      recurringCredit: validators.checkRecurringCredit(result[5]),
      client: clientId,
      type: {
        id: validators.checkTypeId(result[6], result[7]),
        name: validators.checkTypeName(result[7]),
      },
      email: result[8],
      mobile: result[9],
      isActive: true,
      updatedAt: serverTimestamp(),
      recurringCreditType: result[13],
      costCenter: result[14],
    };

    jsonObjectId.push(result[0].toString());
    jsonObjectArray.push(jsonObject);
    count++;
  });
  return [jsonObjectId, jsonObjectArray, consoleError];
};

export const getCompanyId = async (clientId) => {
  try {
    const userDoc = doc(db, "users", clientId);
    const snapshot = await getDoc(userDoc);

    if (snapshot.exists()) {
      const data = snapshot.data();
      return data?.client?.id || null;
    } else {
      console.error("No such document!");
      return null;
    }
  } catch (err) {
    console.error("Error getting document:", err);
    return "";
  }
};

export const productType = async (m) => {
  const productId = m.Product?.categoryId;

  if (!productId) {
    m.productTypeName = "Invalid Product ID";
  }

  try {
    const productDocRef = doc(db, "categories", productId);
    const getProductTypeSnap = await getDoc(productDocRef);

    if (getProductTypeSnap.exists()) {
      const productType = getProductTypeSnap.data().name;
      m.productTypeName = productType;
    } else {
      m.productTypeName = "Product Type Not Found";
    }
  } catch (err) {
    console.log("Error fetching product type:", err);
    m.productTypeName = "Error Fetching Product Type";
  }
};

export const renderDate = (record) => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const date = record?.Date;

  if (date instanceof Date) {
    const time = `${date.getDate()} ${
      months[date.getMonth()]
    } ${date.getFullYear()}`;
    return time;
  }

  if (date?.seconds) {
    const jsDate = new Date(date.seconds * 1000);
    const time = `${jsDate.getDate()} ${
      months[jsDate.getMonth()]
    } ${jsDate.getFullYear()}`;
    return time;
  }

  try {
    const parsedDate = new Date(date);
    if (!isNaN(parsedDate.getTime())) {
      const time = `${parsedDate.getDate()} ${
        months[parsedDate.getMonth()]
      } ${parsedDate.getFullYear()}`;
      return time;
    }
  } catch (err) {
    console.error("Error parsing date:", err);
  }

  return "N/A";
};

export const renderExpiryDate = (value) => {
  const seconds = value?.product?.expiryDate?.seconds;
  if (!seconds) return "N/A";
  const date = new Date(seconds * 1000);
  return date.toLocaleDateString();
};

export const renderTime = (record) => {
  const time = record?.Date;

  if (time) {
    let dateTime;
    if (time.seconds) {
      dateTime = new Date(time.seconds * 1000);
    } else if (typeof time === "string" || typeof time === "number") {
      dateTime = new Date(time);
    } else {
      dateTime = time;
    }

    if (dateTime instanceof Date && !isNaN(dateTime.getTime())) {
      const hours = String(dateTime.getHours()).padStart(2, "0");
      const minutes = String(dateTime.getMinutes()).padStart(2, "0");
      const seconds = String(dateTime.getSeconds()).padStart(2, "0");
      return `${hours}:${minutes}:${seconds}`;
    }
  }
  return "N/A";
};

export const renderDateTime = (record) => {
  const dateTime = new Date(record.Date);

  const options = {
    timeZone: "Asia/Kuala_Lumpur",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  };

  const formatted = new Intl.DateTimeFormat("en-GB", options).format(dateTime);
  return formatted;
};

export const getProductName = (record) => {
  const productName = record?.Product?.name ?? null;
  if (productName === null) return "N/A";
  return productName;
};

export const getPrice = (record) => {
  const price = record?.Amount ?? null;
  if (price === null) return "N/A";
  return price;
};

export const employeeType = async (m) => {
  const employeeId = m.Employee?.id;

  if (!employeeId) {
    m.employeeTypeName = "";
    return;
  }

  try {
    const employeeDocRef = doc(db, "employees", employeeId);
    const getEmployeeTypeSnap = await getDoc(employeeDocRef);

    if (getEmployeeTypeSnap.exists()) {
      const employeeType = getEmployeeTypeSnap.data();
      const getEmployeeType = employeeType?.type?.name;

      m.employeeTypeName = getEmployeeType
        ? getEmployeeType.toString()
        : "Type Not Defined";
      m.employee = employeeType;
    } else {
      m.employeeTypeName = "Employee Type Not Found";
      m.employee = "";
    }
  } catch (err) {
    console.log("Error fetching employee type:", err);
    m.employeeTypeName = "Error Fetching Employee Type";
    m.employee = "";
  }
};

export const productDetails = async (m) => {
  const productId = m.Product?.id;

  if (!productId) {
    m.productName = "Invalid Product ID";
    m.productSku = "Invalid Product ID";
    m.productSerial = "Invalid Product ID";
  }

  try {
    const productDocRef = doc(db, "products", productId);
    const productSnapshot = await getDoc(productDocRef);

    if (productSnapshot.exists()) {
      const product = productSnapshot.data();
      const getName = product?.name;
      const getSku = product?.sku;
      const getSerial = product?.serialNumber;

      m.productName = getName ? getName.toString() : "";
      m.productSku = getSku ? getSku.toString() : "";
      m.productSerial = getSerial ? getSerial.toString() : "";
    } else {
      m.productName = "N/A";
      m.productSku = "N/A";
      m.productSerial = "N/A";
    }
  } catch (err) {
    console.error("Error fetching product details:", err);
    m.productName = "Error fetching product details";
    m.productSku = "Error fetching product details";
    m.productSerial = "Error fetching product details";
  }
};

export const getEmployeeType = async (companyId) => {
  try {
    const employeeTypeRef = collection(db, "employeeTypes");
    const q = companyId
      ? query(employeeTypeRef, where("client", "==", companyId))
      : employeeTypeRef;
    const employeeSnapshot = await getDocs(q);

    const datas = employeeSnapshot.docs.map((doc) => ({
      type_id: doc.id,
      type_name: doc.data().name,
    }));

    return datas;
  } catch (err) {
    console.error("Error fetching employee types:", err);
    return [];
  }
};

export const transactionStatus = (record) => {
  if (!record || record.status === null) return "Others";

  switch (record.status) {
    case 1:
      return "Successful";
    case 0:
      return "Failed";
    case -1:
      return "Canceled";
    case -2:
      return "Dropped";
    case -3:
      return "Void";
    default:
      return "Others";
  }
};

export const renderTransactionStatus = (record) => {
  if (!record) {
    return <Typography style={{ fontSize: "0.875rem" }}>Others</Typography>;
  }
  const statusValue = record.Status ?? record.status;
  const parsedStatus =
    typeof statusValue === "string" ? Number(statusValue) : statusValue;

  if (
    parsedStatus === null ||
    parsedStatus === undefined ||
    isNaN(parsedStatus)
  ) {
    return <Typography style={{ fontSize: "0.875rem" }}>Others</Typography>;
  }

  switch (statusValue) {
    case 1:
      return (
        <Typography style={{ color: "#00C07F", fontSize: "0.875rem" }}>
          Successful
        </Typography>
      );
    case 0:
      return (
        <Typography style={{ color: "#FF6562", fontSize: "0.875rem" }}>
          Failed
        </Typography>
      );
    case -1:
      return (
        <Typography style={{ color: "#C0C0C0", fontSize: "0.875rem" }}>
          Canceled
        </Typography>
      );
    case -2:
      return (
        <Typography style={{ color: "#FF6562", fontSize: "0.875rem" }}>
          Dropped
        </Typography>
      );
    case -3:
      return (
        <Typography style={{ color: "#FF6562", fontSize: "0.875rem" }}>
          Void
        </Typography>
      );
    default:
      return <Typography style={{ fontSize: "0.875rem" }}>Others</Typography>;
  }
};

export const statusForExcelReport = (status) => {
  switch (status) {
    case 1:
      return "Successful";
    case 0:
      return "Failed";
    case -1:
      return "Canceled";
    case -2:
      return "Dropped";
    case -3:
      return "Void";
  }
};

export const getTotalCashReceived = (record) =>
  record?.CashInDetails?.cashInTotal ?? "";

export const getTotalCashReturned = (record) =>
  record?.CashOutDetails?.cashOutTotal ?? "";

export const getTransId = (record) => {
  const transId = record?.Payment?.transactionId ?? null;
  if (transId === null) return "";
  return transId;
};

export const getRefId = (record) => {
  const refNo = record?.Payment?.refNo ?? null;
  if (refNo === null) return "";
  return refNo;
};

export const getPriceInTransaction = async (record) => {
  const priceInTransaction = record?.product?.price;

  if (priceInTransaction === null) {
    return "-";
  }

  return priceInTransaction;
};

export const renderProductSlot = (record) => {
  let rowArray = ["A", "B", "C", "D", "E", "F", "G", "H"];
  if (record?.Product?.slot === null || record?.Product?.slot === undefined)
    return "N/A";
  let productSlot = record?.Product?.slot;
  console.log("productSlot", productSlot);
  if (productSlot === null) return "N/A";
  let row = Math.floor((productSlot - 1) / 12);
  let column = ((productSlot - 1) % 12) + 1;
  return `${rowArray[row]}${column}`;
};

const TRANSACTION = {
  GATEWAY: {
    234: "Alipay Scan",
    305: "WeChatPay CN Scan",
    320: "Boost Scan",
    329: "MCashMS",
    336: "TNG Scan",
    338: "UnionPay Scan",
    347: "GrabPay QR",
    354: "MaybankQR Scan",
    379: "GrabPay Scan",
    164: "PrestoPay Scan",
    19: "ShopeePay Scan",
    1247: "Setel Pay Scan",
    733: "Alipay Scan",
  },
};

export const renderGatewayField = (record) => {
  if (record?.Payment?.gatewayID) {
    return TRANSACTION.GATEWAY[record?.Payment?.gatewayID];
  } else if (record?.Payment?.cash) {
    return "Cash";
  } else if (record?.Payment?.terminal) {
    return "Paywave";
  } else if (
    record?.Payment?.ghlPaymentDetail?.paymentResponse?.msg?.productCode
  ) {
    return record.Payment.ghlPaymentDetail.paymentResponse.msg.productCode.toString();
  } else {
    return "Others";
  }
};

export const checkMachineDualMode = async (clientId) => {
  try {
    const q = query(
      collection(db, "machines"),
      where("client.id", "==", clientId),
    );
    const querySnapshot = await getDocs(q);

    for (const doc of querySnapshot.docs) {
      const data = doc.data();
      const machineParams = data?.machineParams;

      if (
        machineParams?.machineDualMode === true ||
        machineParams?.mroDualMode === true
      ) {
        return true;
      }
    }

    return false;
  } catch (err) {
    console.error(err);
  }
};

export const getEmployeeDocs = async (clientId) => {
  try {
    const q = query(
      collection(db, "employees"),
      where("client", "==", clientId),
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error("No employees found.");
    }

    const employees = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return employees;
  } catch (err) {
    console.error(err);
  }
};

export const getProductDocs = async (clientId) => {
  try {
    const q = query(
      collection(db, "products"),
      where("client", "==", clientId),
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error("No products found");
    }

    const products = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return products;
  } catch (err) {
    console.error(err);
  }
};

export const getProductCategoryDocs = async (clientId) => {
  try {
    const q = query(
      collection(db, "categories"),
      where("client", "==", clientId),
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error("No product categories found");
    }

    const categories = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return categories;
  } catch (err) {
    console.error(err);
  }
};

export const renderDateMY = (record, { locale = "en-MY" } = {}) => {
  const d = record?.date instanceof Date ? record.date : new Date(record?.date);
  if (isNaN(d)) return "N/A";

  return new Intl.DateTimeFormat(locale, {
    timeZone: "Asia/Kuala_Lumpur",
    weekday: "short",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(d);
};

export const renderTImeMY = (record, { locale = "en-MY" } = {}) => {
  const d = record?.date instanceof Date ? record.date : new Date(record?.date);
  if (isNaN(d)) return "N/A";

  return new Intl.DateTimeFormat(locale, {
    timeZone: "Asia/Kuala_Lumpur",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(d);
};

export const getTransactionId = (record) => {
  return (
    record?.payment?.transactionId ??
    record?.payment?.ghlPaymentDetail?.paymentResponse?.msg?.txnRef ??
    ""
  );
};

export const getReferenceNo = (record) => {
  return (
    record?.payment?.refNo ??
    record?.payment?.ghlPaymentDetail?.paymentResponse?.msg?.retTxnRef ??
    ""
  );
};

export const getPyamentType = (post) => {
  try {
    if (post?.payment?.gatewayID) {
      return TRANSACTION[post.payment.gatewayID] ?? post.payment.gatewayID;
    }
    if (post?.payment?.cash) return "Cash Coin";
    if (post?.payment?.terminal) return "Paywave";
    if (post?.payment?.ghlPaymentDetail?.paymentResponse?.msg?.productCode) {
      return post.payment.ghlPaymentDetail.paymentResponse.msg.productCode;
    }
    if (post?.payment?.virclePaymentDetail) return "Vircle";
    if (post?.payment?.onePay) return "1Pay";

    if (post?.mode === "mro" || post?.payment?.paymentType === "mro") {
      return post?.payment?.paymentType || "mro";
    }

    return "";
  } catch (error) {
    return "";
  }
};

export const getEmployee = (post, employees = []) => {
  try {
    const employeeId = post?.employee?.id;
    const matchDoc = employees.find((doc) => doc.id === employeeId);

    return {
      department: matchDoc?.type?.name ?? null,
      cardNumber: matchDoc?.cardNumber ?? null,
    };
  } catch {
    return {
      department: null,
      cardNumber: null,
    };
  }
};

export const getProduct = (post, products = []) => {
  try {
    const productId = post?.product?.id;
    const matchDoc = products.find((doc) => doc.id === productId);

    return {
      sku: matchDoc?.sku ?? "",
      serialNumber: matchDoc?.serialNumber ?? "",
    };
  } catch {
    return {};
  }
};

export const getProductCategory = (post, categories = []) => {
  try {
    const categoryId = post?.product?.categoryId;
    const matchDoc = categories.find((doc) => doc.id === categoryId);

    return {
      category: matchDoc?.name ?? "",
    };
  } catch {
    return {
      category: "",
    };
  }
};

export const getFormattedSlotId = (post) => {
  const slotId = post?.product?.slot ?? post?.product?.slotId;
  if (slotId === undefined || slotId === null) return "";

  const rowArray = ["A", "B", "C", "D", "E", "F"];
  const row = Math.floor(slotId / 12);
  const column = slotId % 12;
  return `${rowArray[row] ?? "X"}${column}`;
};

export const getStatus = (post) => {
  const status = post?.Status;
  switch (status) {
    case 1:
      return "Successful";
    case -1:
      return "Canceled";
    case -2:
      return "Dropped";
    case -3:
      return "Void";
    case -4:
      return "DispensingError";
    default:
      return "Failed";
  }
};

export const getErrorCategoryMessage = (post) => {
  const errorDescription = post?.error?.description ?? "";
  if (!errorDescription) return { category: "", message: "" };

  const desc = errorDescription.toLowerCase();
  const matchDetail = ErrorDetailList.find((err) =>
    err.key.some((k) => desc.includes(k.toLowerCase())),
  );

  if (!matchDetail) return { category: "", message: "" };

  const isDriverBoardKey =
    matchDetail.key.includes("driver board went wrong") ||
    matchDetail.key.includes("no item detect dropped");

  if (isDriverBoardKey) {
    const response = post?.responseStatus ?? {};
    const { globalDroppedSensor, itemDroppedSensor, motorTurned, itemDropped } =
      response;

    if (!globalDroppedSensor && !itemDroppedSensor) {
      return {
        category: matchDetail.category,
        message: `${matchDetail.message} — sensor off`,
      };
    }
    if (!motorTurned) {
      return {
        category: matchDetail.category,
        message: `${matchDetail.message} — motor didn’t spin`,
      };
    }
    if (!itemDropped) {
      return {
        category: matchDetail.category,
        message: `${matchDetail.message} — sensor didn’t detect drop`,
      };
    }
  }

  return { category: matchDetail.category, message: matchDetail.message };
};

export const getSanitizedErrorDetail = (value) => {
  const raw = value?.error?.description ?? "";
  return `${raw
    .replaceAll("com.vechnology.ve.models.payment.", "*")
    .replaceAll("com.vechnology.ve.models.serial.driverBoard.", "*")
    .replaceAll("com.vechnology.ve", "*")
    .substring(0, 100)}...`;
};

export const setData = (obj, path, value) => {
  if (typeof path !== "string") return;

  const keys = path.split(".");
  let current = obj;

  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      current[key] = value;
    } else {
      if (!(key in current) || typeof current[key] !== "object") {
        current[key] = {};
      }
      current = current[key];
    }
  });
};
